import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cloud, RefreshCw, PlugZap, Save, Trash2, Coins } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-wider">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

const Panel = ({ title, desc, children, action }) => (
  <div className="min-w-0 border border-slate-200 bg-white">
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
      <div className="min-w-0">
        <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
        {desc && <p className="mt-1 max-w-2xl text-xs text-slate-500">{desc}</p>}
      </div>
      {action}
    </div>
    <div className="min-w-0 p-5">{children}</div>
  </div>
);

const SharePointTab = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const [test, setTest] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const [c, h] = await Promise.all([api.get("/sharepoint/config"), api.get("/sharepoint/history")]);
    setCfg(c.data);
    setHistory(h.data);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/sharepoint/config", { ...cfg, hour_utc: Number(cfg.hour_utc) || 2 });
      setCfg(data);
      toast.success("SharePoint settings saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const runTest = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/sharepoint/test");
      setTest(data);
      toast.success(`Connected to ${data.file_name}`);
    } catch (e) {
      setTest(null);
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const syncNow = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/sharepoint/sync");
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const saveMappingFromTest = async (field, col) => {
    const mapping = { ...(test?.suggested_mapping || {}), [field]: col === "__none__" ? null : col };
    setTest({ ...test, suggested_mapping: mapping });
    setCfg({ ...cfg, mapping });
  };

  if (!cfg) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <Panel
        title="Azure app credentials"
        desc="Register an app in Microsoft Entra (app-only), grant Sites.Read.All or Sites.Selected with admin consent, then paste the values here. Secrets stay on the server."
        action={
          <div className="flex items-center gap-3">
            <Badge
              variant="outline"
              className={`rounded-md border ${
                cfg.configured
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-amber-200 bg-amber-50 text-amber-700"
              }`}
            >
              {cfg.configured ? "credentials saved" : "not configured"}
            </Badge>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="sp-enabled-switch"
                checked={!!cfg.enabled}
                onCheckedChange={set("enabled")}
              />
              <span className="text-xs font-semibold uppercase tracking-wider">Auto sync</span>
            </div>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Tenant ID">
            <Input
              data-testid="sp-tenant-input"
              className="h-10 border-slate-300"
              value={cfg.tenant_id}
              onChange={(e) => set("tenant_id")(e.target.value)}
            />
          </Field>
          <Field label="Client ID">
            <Input
              data-testid="sp-client-input"
              className="h-10 border-slate-300"
              value={cfg.client_id}
              onChange={(e) => set("client_id")(e.target.value)}
            />
          </Field>
          <Field label="Client Secret" hint={cfg.configured ? "A secret is stored on the server — leave blank to keep it, or paste a new one to replace." : "Paste the secret Value, not the Secret ID."}>
            <Input
              data-testid="sp-secret-input"
              type="password"
              placeholder={cfg.configured ? "•••••••• (saved)" : ""}
              className="h-10 border-slate-300"
              value={cfg.client_secret === "********" ? "" : cfg.client_secret}
              onChange={(e) => set("client_secret")(e.target.value)}
            />
          </Field>
        </div>
      </Panel>

      <Panel title="Where is the sheet?" desc="Either paste the full SharePoint file link, or point at the site, library and file path.">
        <div className="mb-4 w-56">
          <Field label="Locate by">
            <Select value={cfg.mode} onValueChange={set("mode")}>
              <SelectTrigger data-testid="sp-mode-select" className="h-10 border-slate-300 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border border-slate-200 bg-white">
                <SelectItem value="link">File link</SelectItem>
                <SelectItem value="path">Site + library + path</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        {cfg.mode === "link" ? (
          <Field label="SharePoint file link">
            <Input
              data-testid="sp-fileurl-input"
              placeholder="https://contoso.sharepoint.com/:x:/r/sites/Ads/Shared%20Documents/daily.xlsx"
              className="h-10 border-slate-300"
              value={cfg.file_url}
              onChange={(e) => set("file_url")(e.target.value)}
            />
          </Field>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Site hostname">
              <Input
                data-testid="sp-host-input"
                placeholder="contoso.sharepoint.com"
                className="h-10 border-slate-300"
                value={cfg.site_hostname}
                onChange={(e) => set("site_hostname")(e.target.value)}
              />
            </Field>
            <Field label="Site path">
              <Input
                data-testid="sp-sitepath-input"
                placeholder="sites/Ads"
                className="h-10 border-slate-300"
                value={cfg.site_path}
                onChange={(e) => set("site_path")(e.target.value)}
              />
            </Field>
            <Field label="Document library">
              <Input
                data-testid="sp-library-input"
                className="h-10 border-slate-300"
                value={cfg.library}
                onChange={(e) => set("library")(e.target.value)}
              />
            </Field>
            <Field label="File path">
              <Input
                data-testid="sp-filepath-input"
                placeholder="Reports/daily.xlsx"
                className="h-10 border-slate-300"
                value={cfg.file_path}
                onChange={(e) => set("file_path")(e.target.value)}
              />
            </Field>
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Worksheet name" hint="Leave blank for the first sheet.">
            <Input
              data-testid="sp-worksheet-input"
              className="h-10 border-slate-300"
              value={cfg.worksheet}
              onChange={(e) => set("worksheet")(e.target.value)}
            />
          </Field>
          <Field label="Schedule">
            <Select value={cfg.schedule} onValueChange={set("schedule")}>
              <SelectTrigger data-testid="sp-schedule-select" className="h-10 border-slate-300 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border border-slate-200 bg-white">
                <SelectItem value="daily">Every morning</SelectItem>
                <SelectItem value="hourly">Every hour</SelectItem>
                <SelectItem value="manual">Manual only</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Hour (UTC)" hint="Used for the daily schedule.">
            <Input
              type="number"
              min="0"
              max="23"
              data-testid="sp-hour-input"
              className="h-10 border-slate-300"
              value={cfg.hour_utc}
              onChange={(e) => set("hour_utc")(e.target.value)}
            />
          </Field>
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button
            data-testid="sp-save-button"
            disabled={busy === "save"}
            className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
            onClick={save}
          >
            <Save className="mr-2 h-4 w-4" /> Save settings
          </Button>
          <Button
            variant="outline"
            data-testid="sp-test-button"
            disabled={busy === "test"}
            className="rounded-md border-slate-300 active:scale-95"
            onClick={runTest}
          >
            <PlugZap className="mr-2 h-4 w-4" /> Test connection
          </Button>
          <Button
            variant="outline"
            data-testid="sp-sync-button"
            disabled={busy === "sync"}
            className="rounded-md border-slate-300 active:scale-95"
            onClick={syncNow}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Sync now
          </Button>
        </div>
        {cfg.last_status && (
          <p className="mt-4 text-xs text-slate-500" data-testid="sp-last-status">
            Last sync {cfg.last_sync ? new Date(cfg.last_sync).toLocaleString() : "—"} ·{" "}
            <span className={cfg.last_status === "success" ? "text-emerald-600" : "text-red-600"}>
              {cfg.last_status}
            </span>{" "}
            · {cfg.last_message}
          </p>
        )}
      </Panel>

      {test && (
        <Panel title={`Column mapping for ${test.file_name}`} desc="Match your SharePoint column headers to the app's fields. Saved with your settings.">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {test.fields.map(({ field, required }) => (
              <Field key={field} label={`${field}${required ? " *" : ""}`}>
                <Select
                  value={test.suggested_mapping?.[field] || "__none__"}
                  onValueChange={(v) => saveMappingFromTest(field, v)}
                >
                  <SelectTrigger data-testid={`sp-map-${field}`} className="h-10 border-slate-300 bg-white">
                    <SelectValue placeholder="Not mapped" />
                  </SelectTrigger>
                  <SelectContent className="border border-slate-200 bg-white">
                    <SelectItem value="__none__">Not mapped</SelectItem>
                    {test.headers.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500">{test.total_rows} rows detected. Save settings to store this mapping.</p>
        </Panel>
      )}

      <Panel title="Sync history">
        <div className="divide-y divide-slate-100" data-testid="sp-history">
          {history.length === 0 && <div className="text-sm text-slate-500">No syncs yet.</div>}
          {history.map((h) => (
            <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
              <span className="text-slate-600">{new Date(h.at).toLocaleString()}</span>
              <span className="truncate">{h.message}</span>
              <Badge
                variant="outline"
                className={`rounded-md border ${
                  h.status === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-red-200 bg-red-50 text-red-700"
                }`}
              >
                {h.status}
              </Badge>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};

const FxTab = () => {
  const [fx, setFx] = useState(null);

  useEffect(() => {
    api.get("/settings/fx").then((r) => setFx(r.data)).catch(() => {});
  }, []);

  const save = async (mode) => {
    try {
      const { data } = await api.put("/settings/fx", {
        mode,
        base: fx.base,
        usd_to_inr: Number(fx.usd_to_inr) || 86,
      });
      setFx({ ...data, usd_to_inr: data.rates.USD });
      toast.success(mode === "live" ? "Live rate fetched" : "Rate saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!fx) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <Panel
      title="Exchange rate"
      desc="Used when you pick the consolidated currency filter, which converts every INR and USD entry into one base currency."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Base currency">
          <Select value={fx.base} onValueChange={(v) => setFx({ ...fx, base: v })}>
            <SelectTrigger data-testid="fx-base-select" className="h-10 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="INR">INR ₹</SelectItem>
              <SelectItem value="USD">USD $</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="1 USD = ? INR">
          <Input
            type="number"
            step="0.01"
            data-testid="fx-rate-input"
            className="h-10 border-slate-300"
            value={fx.usd_to_inr}
            onChange={(e) => setFx({ ...fx, usd_to_inr: e.target.value })}
          />
        </Field>
        <Field label="Source" hint={fx.updated_at ? `Updated ${new Date(fx.updated_at).toLocaleString()}` : "Default rate"}>
          <div className="flex h-10 items-center text-sm font-semibold">{fx.source}</div>
        </Field>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        <Button
          data-testid="fx-save-button"
          className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
          onClick={() => save("manual")}
        >
          <Save className="mr-2 h-4 w-4" /> Save manual rate
        </Button>
        <Button
          variant="outline"
          data-testid="fx-live-button"
          className="rounded-md border-slate-300 active:scale-95"
          onClick={() => save("live")}
        >
          <Coins className="mr-2 h-4 w-4" /> Fetch live rate
        </Button>
      </div>
    </Panel>
  );
};

const MappingsTab = () => {
  const [rows, setRows] = useState([]);

  const load = () => api.get("/mappings").then((r) => setRows(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/mappings/${id}`);
      toast.success("Preset deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Panel title="Saved column mappings" desc="Reusable header mappings you saved while uploading a sheet.">
      <div className="divide-y divide-slate-100" data-testid="mapping-presets">
        {rows.length === 0 && <div className="text-sm text-slate-500">No presets saved yet.</div>}
        {rows.map((m) => (
          <div key={m.id} className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">{m.name}</div>
              <div className="mt-1 truncate text-xs text-slate-500">
                {Object.entries(m.mapping || {})
                  .filter(([, v]) => v)
                  .map(([k, v]) => `${k} ← ${v}`)
                  .join(" · ")}
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              data-testid={`delete-mapping-${m.id}`}
              className="h-8 w-8 text-red-600 hover:bg-red-50"
              onClick={() => remove(m.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </Panel>
  );
};

export default function Settings() {
  return (
    <div className="min-w-0 max-w-full overflow-x-hidden" data-testid="settings-page">
      <div className="mb-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          Automation
        </div>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">Settings</h1>
      </div>
      <Tabs defaultValue="sharepoint">
        <TabsList className="mb-6 flex w-full max-w-full overflow-x-auto rounded-md bg-slate-100">
          <TabsTrigger value="sharepoint" data-testid="tab-sharepoint" className="rounded-md">
            <Cloud className="mr-2 h-4 w-4" /> SharePoint Sync
          </TabsTrigger>
          <TabsTrigger value="fx" data-testid="tab-fx" className="rounded-md">
            Exchange Rate
          </TabsTrigger>
          <TabsTrigger value="mappings" data-testid="tab-mappings" className="rounded-md">
            Column Mappings
          </TabsTrigger>
        </TabsList>
        <TabsContent value="sharepoint">
          <SharePointTab />
        </TabsContent>
        <TabsContent value="fx">
          <FxTab />
        </TabsContent>
        <TabsContent value="mappings">
          <MappingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
